import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from './configuration.service';
import { forkJoin, Observable, switchMap, map } from 'rxjs';
import { DriftConfiguration } from '../models/configuration.interface';
import {
  ReviewSession as ReviewSession,
  ReviewSessionPost as ReviewSessionPost,
  normalizePersonaId
} from '../models/review.interface';
import { CollectionsLinks } from '../models/collection.interface';

@Injectable({
  providedIn: 'root'
})
export class ReviewService {
  config: DriftConfiguration;

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService
  ) {
    this.config = this.configurationService.getConfiguration();
  }

  createReviewSession(documentName: string, personaIds: string[]) {
    const reviewSession: ReviewSessionPost = {
      persona_id: personaIds,
      document: documentName
    };
    console.log(reviewSession);
    return this.http.post<ReviewSession>(`${this.config.driftApi}/reviews`, reviewSession).pipe(
      map(response => ({
        ...response,
        persona_id: normalizePersonaId(response.persona_id)
      }))
    );
  }

  getRecentlyReviewed(): Observable<ReviewSession[]> {
    // Add cache-busting headers to ensure fresh data
    const headers = {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0'
    };
    return this.http.get<ReviewSession[]>(`${this.config.driftApi}/reviews`, { headers }).pipe(
      map(reviews =>
        reviews.map(review => ({
          ...review,
          persona_id: normalizePersonaId(review.persona_id)
        }))
      )
    );
  }

  getReviewSession(review_id: string): Observable<ReviewSession> {
    // Add cache-busting headers to ensure fresh data
    const headers = {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0'
    };
    return this.http.get<ReviewSession>(`${this.config.driftApi}/reviews/${review_id}`, { headers }).pipe(
      map(response => ({
        ...response,
        persona_id: normalizePersonaId(response.persona_id)
      }))
    );
  }

  updateReviewSession(reviewSession: ReviewSession): Observable<ReviewSession> {
    return this.http
      .put<ReviewSession>(`${this.config.driftApi}/reviews/${reviewSession.review_id}`, reviewSession)
      .pipe(
        map(response => ({
          ...response,
          persona_id: normalizePersonaId(response.persona_id)
        }))
      );
  }

  uploadFilesToReview(id: string, files: FileList): Observable<object> {
    const fileArray = Array.from(files);
    const data = { files: fileArray.map(file => file.name) };

    return this.http.post<CollectionsLinks>(`${this.config.driftApi}/reviews/${id}/links`, data).pipe(
      switchMap(response => {
        const httpSessions: Observable<object>[] = [];
        response.links.forEach(link => {
          const file = fileArray.find(f => f.name === link.file_name);
          if (file) {
            httpSessions.push(this.http.put(link.url, file, { reportProgress: true, observe: 'events' }));
          } else {
            console.error(`Upload details missing for file: ${link.file_name}`);
          }
        });
        return forkJoin(httpSessions);
      })
    );
  }

  /**
   * Update the resolved status of a specific finding
   * @param reviewId - The review session ID
   * @param insightId - The unique insight/finding ID
   * @param resolved - The new resolved status (true/false)
   * @returns Observable of the API response
   */
  updateFindingStatus(reviewId: string, insightId: string, resolved: boolean): Observable<{ resolved: boolean }> {
    const url = `${this.config.driftApi}/reviews/${reviewId}/findings/${insightId}`;
    return this.http.patch<{ resolved: boolean }>(url, { resolved });
  }

  /**
   * Export review findings to Excel
   * @param reviewId - The review session ID
   * @returns Observable of object containing the Blob and filename
   */
  exportReviewToExcel(reviewId: string): Observable<{ blob: Blob; filename: string }> {
    const url = `${this.config.driftApi}/reviews/${reviewId}/export`;
    return this.http
      .get(url, {
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map(response => {
          // Extract filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          let filename = 'review_export.xlsx'; // Default filename

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
            }
          }

          return {
            blob: response.body as Blob,
            filename: filename
          };
        })
      );
  }
}
